import { ApiTransaction } from '@vend/types'
import { trackUserEvent } from '@vend/utilities'
import { EffectsMap } from 'use-effect-reducer'
import { WEB_REGISTER } from '../../../../web-register.constant'
import { connectToTerminal } from '../../helpers/connectToTerminal'
import { isCancellation } from '../../helpers/errorChecking'
import {
  LSPAYPairingData,
  LSPAYPairingRequest,
  SwiperError,
} from '../../helpers/lspay.types'
import {
  FAILED_INTERACT,
  FAILED_PAIRING_CHECK,
  FAILED_REFUND,
  FAILED_TERMINAL_CONNECTION,
  SIMULATOR_SN,
  STRIPE_ERROR_CODE,
  SWIPER_ERROR_CODES,
} from '../../helpers/lspayConstants'
import { refund, refundWithTerminal } from '../../helpers/refund'
import { GET_PAIRING_DATA } from '../../queries/Pairings'
import { persistRefund } from './helpers'
import {
  RefundEffect,
  RefundEffectsMapArgs,
  RefundEvent,
  RefundState,
} from './types'

export const getEffectForRetry = (
  state: RefundState
): RefundEffect | undefined => {
  switch (state.status) {
    case 'refundingViaCardNotPresent': {
      return {
        type: 'cardNotPresentEffect',
      }
    }
    case 'refundingViaCardPresent': {
      return {
        type: 'cardPresentEffect',
        chargeId: state.chargeId,
        terminal: state.terminal,
        terminalMonitoringInstance: state.terminalMonitoringInstance,
        isRetry: true,
      }
    }
    case 'fetchingPairing': {
      return {
        type: 'fetchPairingEffect',
      }
    }
    case 'connectingToTerminal': {
      return {
        type: 'connectTerminalEffect',
        pairingSN: state.pairing.sn,
        isSimulated: state.pairing.isSimulated,
        terminalID: state.pairing.terminalID,
        terminalModel: state.pairing.terminalModel,
      }
    }
    case 'persistingCardPresentRefund': {
      return {
        type: 'persistCardPresentEffect',
        refundMethod: state.refundMethod,
      }
    }
  }
}

export const getRefundEffectsMap = ({
  amount,
  currency,
  outletId,
  originalRegisterSalePaymentId,
  transactionId,
  retailerPaymentType,
  register,
  trackError,
  trackTiming,
  isSimulated,
  apolloClient,
  isSandbox,
}: RefundEffectsMapArgs): EffectsMap<
  RefundState,
  RefundEvent,
  RefundEffect
> => {
  const { id: registerId, name: registerName } = register

  return {
    cardNotPresentEffect: (_state, _effect, dispatch) => {
      refund({
        registerID: registerId,
        registerName,
        currency,
        retailerPaymentTypeID: retailerPaymentType.id,
        registerSalePaymentID: transactionId,
        outletId,
        amount,
        trackTiming,
        originalRegisterSalePaymentId,
      })
        .then(transaction => {
          dispatch({ type: 'CARD_NOT_PRESENT_OK', transaction })
        })
        .catch((error: SwiperError) => {
          dispatch({
            type: 'CARD_NOT_PRESENT_ERROR',
            error,
            registerId,
          })

          // If its an interac error just send a UBE
          if (
            error.code ===
            SWIPER_ERROR_CODES.LSPAY_CARD_NOT_PRESENT_INTERAC_REFUND
          ) {
            trackUserEvent(WEB_REGISTER.DISPLAY_NAME, FAILED_INTERACT, {
              error,
              transactionId,
              originalRegisterSalePaymentId,
            })
            return
          }

          // else we track an error
          trackError(FAILED_REFUND, error)
        })
    },
    fetchPairingEffect: (_state, _effect, dispatch) => {
      if (isSimulated) {
        dispatch({
          type: 'PAIRING_OK',
          pairing: {
            sn: SIMULATOR_SN,
            label: 'simulator',
            isSimulated,
          },
        })
        return
      }

      apolloClient
        .query<LSPAYPairingData, LSPAYPairingRequest>({
          query: GET_PAIRING_DATA,
          variables: {
            registerID: registerId,
          },
        })
        .then(result => {
          const pairing = result.data.lspayPairings[0]
          if (pairing) {
            const { deviceSN, deviceLabel, deviceID, deviceType } = pairing
            dispatch({
              type: 'PAIRING_OK',
              pairing: {
                sn: deviceSN,
                label: deviceLabel,
                isSimulated: false,
                terminalID: deviceID,
                terminalModel: deviceType,
              },
            })
          }

          dispatch({ type: 'PAIRING_ERROR', pairingIsMissing: true })
        })
        .catch(error => {
          // Distinguish network errors (HTTP 4xx or 5xx, or a different error code) from a missing pairing
          const errorCode = error?.graphQLErrors?.[0]?.extensions?.code
          const pairingIsMissing =
            errorCode === SWIPER_ERROR_CODES.CODE_REGISTER_PAIRING_NOT_FOUND
          dispatch({
            type: 'PAIRING_ERROR',
            pairingIsMissing,
          })
          if (!pairingIsMissing) {
            trackError(FAILED_PAIRING_CHECK, error)
          }
        })
    },
    connectTerminalEffect: (_state, effect, dispatch) => {
      connectToTerminal(
        {
          outletId,
          registerId,
          pairingSN: effect.pairingSN,
          isSimulated: effect.isSimulated,
          terminalID: effect.terminalID,
          terminalModel: effect.terminalModel,
        },
        isSandbox,
        trackTiming,
        true
      )
        .then(({ terminal, terminalMonitoringInstance }) => {
          dispatch({
            type: 'TERMINAL_OK',
            terminal,
            terminalMonitoringInstance,
          })
        })
        .catch(error => {
          dispatch({ type: 'TERMINAL_ERROR', error })
          trackError(FAILED_TERMINAL_CONNECTION, error, undefined, [
            STRIPE_ERROR_CODE.READER_ERROR,
          ])
        })
    },
    cardPresentEffect: (_state, effect, dispatch) => {
      const { chargeId, terminal, terminalMonitoringInstance, isRetry } = effect
      refundWithTerminal(
        chargeId,
        amount,
        currency,
        terminal,
        terminalMonitoringInstance,
        trackTiming,
        isRetry
      )
        .then(() => {
          dispatch({ type: 'CARD_PRESENT_OK' })
        })
        .catch(error => {
          dispatch({ type: 'CARD_PRESENT_ERROR', error })

          // if it was a cancellation record a UBE
          if (isCancellation(error)) {
            trackUserEvent(
              WEB_REGISTER.DISPLAY_NAME,
              '[lspay] Card present refund cancelled.',
              {
                error,
                transactionId,
                originalRegisterSalePaymentId,
              }
            )
            return
          }

          // otherwise track the error
          trackError('[lspay] card present refund processing error', error)
        })
    },
    persistCardPresentEffect: (_state, effect, dispatch) => {
      persistRefund({
        registerID: registerId,
        registerName,
        currency,
        retailerPaymentTypeID: retailerPaymentType.id,
        registerSalePaymentID: transactionId,
        originalRegisterSalePaymentId,
        outletId,
        amount,
        trackTiming,
        refundMethod: { type: effect.refundMethod, chargeId: '' },
      })
        .then(transaction =>
          dispatch({ type: 'PERSISTING_CARD_PRESENT_OK', transaction })
        )
        .catch(error => {
          // Create a transaction which is as similar to the one returned from swiper as possible.
          const transaction: ApiTransaction = {
            id: '', // we don't have a swiper tender id as this was generated locally
            register_sale_payment_id: transactionId,
            tenders: [
              {
                id: '', // we don't have a swiper tender id as this was generated locally
                external_transaction_id: '', // currently swiper returns this empty so we can leave it empty here
                currency,
                amount: amount.abs().toNumber(),
                emv: {
                  tags: null,
                },
                card_details: {
                  card: {},
                },
                type: 'REFUND',
                status: 'REFUNDED',
                provider_fields: {
                  lspay_refund_method: 'interac',
                  lspay_note: 'not stored in swiper',
                },
                created_at: new Date().toISOString(),
              },
            ],
            register_id: registerId,
            retailer_payment_type_id: retailerPaymentType.id,
            payment_type_id: retailerPaymentType.type_id,
            status: 'REFUNDED',
            provider_fields: {},
          }

          trackError(
            '[lspay] There was an error saving this refund in swiper. Using the fallback mechanism.',
            error,
            { transaction }
          )

          dispatch({ type: 'PERSISTING_CARD_PRESENT_OK', transaction })
        })
    },
  }
}
